import React from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';
import { TransactionalSolidButton } from '@bibliocommons/base-buttons';
import { ModalHeader, ModalContent, ModalFooter } from '@bibliocommons/modal';
import EventsActions from 'app/actions/EventsActions';
import { eventShape } from 'app/shapes/events';

export default function RemoveRegistrationModal({
  event,
  registrationId,
  registeredEvents,
  removeFromWaitlist,
  removeFromMyEvents,
  removeFromInterested,
  handleClose
}) {
  const dispatch = useDispatch();
  const isSingleEventRegistration = registeredEvents?.size === 1;

  function handleRemoveRegistration() {
    if (removeFromInterested) {
      const eventId = event?.get('id');
      const seriesId = event?.get('seriesId');
      dispatch(EventsActions.removeFromInterestedEvents({ eventId, seriesId, registrationId }));
      handleClose();
      return;
    }

    if (removeFromMyEvents) {
      dispatch(
        EventsActions.removeFromMyEvents({
          registrationId,
          postSuccess: () => {
            handleClose();
          }
        })
      );
      handleClose();
    } else {
      dispatch(
        EventsActions.cancelRegistration({
          registrationId,
          removeFromWaitlist,
          postSuccess: () => {
            handleClose();
          }
        })
      );
      handleClose();
    }
  }

  function renderModalTitle() {
    if (removeFromMyEvents || removeFromInterested) {
      return <FormattedMessage id="remove_from_my_events" />;
    }

    if (removeFromWaitlist) {
      return <FormattedMessage id="remove_from_waitlist" />;
    }

    return <FormattedMessage id="cancel_registration" />;
  }

  function renderModalContent() {
    if (removeFromMyEvents || removeFromInterested) {
      if (isSingleEventRegistration) {
        return <FormattedMessage id="registration.remove_from_my_events.single_event" />;
      }
      return (
        <FormattedMessage
          id="registration.remove_from_my_events.series_event"
          values={{ eventCount: registeredEvents?.size }}
        />
      );
    }

    if (removeFromWaitlist) {
      if (isSingleEventRegistration) {
        return <FormattedMessage id="registration.remove_waitlist.single_event" />;
      }
      return (
        <FormattedMessage
          id="registration.remove_waitlist.series_event"
          values={{ eventCount: registeredEvents?.size }}
        />
      );
    }

    if (isSingleEventRegistration) {
      return <FormattedMessage id="registration.cancel.single_event" />;
    }
    return <FormattedMessage id="registration.cancel.series_event" values={{ eventCount: registeredEvents?.size }} />;
  }

  function renderActionButtonText() {
    if (removeFromMyEvents || removeFromInterested) {
      return <FormattedMessage id="remove_from_my_events" />;
    }

    if (removeFromWaitlist) {
      return <FormattedMessage id="remove_from_waitlist" />;
    }

    return <FormattedMessage id="cancel_registration" />;
  }

  return (
    <div className="cp-remove-registration-modal">
      <ModalHeader title={renderModalTitle()} />
      <ModalContent>{renderModalContent()}</ModalContent>
      <ModalFooter
        primaryActionButtonText={renderActionButtonText()}
        primaryActionButtonTag={TransactionalSolidButton}
        secondaryActionButtonText={<FormattedMessage id="button_nevermind_close" />}
        handlePrimaryAction={handleRemoveRegistration}
      />
    </div>
  );
}

RemoveRegistrationModal.propTypes = {
  event: eventShape,
  registrationId: PropTypes.string.isRequired,
  registeredEvents: ImmutablePropTypes.list.isRequired,
  removeFromWaitlist: PropTypes.bool,
  removeFromMyEvents: PropTypes.bool,
  removeFromInterested: PropTypes.bool,
  handleClose: PropTypes.func.isRequired
};
